import { useMemo, useRef, useState } from 'react';
import { formatDate } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Eyebrow, Skeleton } from '../../../ds/components';
import { useEscapeKey, useFocusTrap } from '../../../ds/hooks';
import { errorMessage } from '../../../lib/api';
import type { StaffPortalCard } from '../portal-api';
import type { DocumentsResponse, DocumentRow } from '../types';
import { Badge } from './bits';
import { PortalDocumentsSidebar, VisibilityCell } from './PortalDocumentsSidebar';
import styles from './DocumentBrowser.module.css';

export interface DocumentBrowserProps {
  open: boolean;
  contactName: string;
  documents: DocumentsResponse | undefined;
  loading: boolean;
  error: unknown;
  onClose: () => void;
  onUpload: () => void;
  onRequest: () => void;
  onOpenRow: (row: DocumentRow) => void;
  /**
   * M7 (Mandantenportal 29): the record's contact and its portal documents (`GET /contacts/:id/portal`) — adds the
   * column „Im Portal sichtbar" (Quelle instead of Herkunft/Stand) and the sidebar; null without the portal.
   */
  portal?: { contactId: string; documents: StaffPortalCard['documents'] } | null | undefined;
  /** „Weiteres Dokument anfordern" of the sidebar (request editor with channel „nur Portal"). */
  onRequestPortal?: (() => void) | undefined;
}

/** Kontaktakte I.06 · the document browser as an overlay inside the record: party/origin navigation + table. */
export function DocumentBrowser({
  open,
  contactName,
  documents,
  loading,
  error,
  onClose,
  onUpload,
  onRequest,
  onOpenRow,
  portal = null,
  onRequestPortal,
}: DocumentBrowserProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [party, setParty] = useState<string>('all');
  const [origin, setOrigin] = useState<string>('all');
  useEscapeKey(onClose, open);
  useFocusTrap(panelRef, open, { initialFocus: 'container' });

  const rows = useMemo(() => {
    const all = documents?.items ?? [];
    return all.filter(
      (row) => (party === 'all' || row.party === party) && (origin === 'all' || row.originKey === origin),
    );
  }, [documents, party, origin]);

  if (!open) return null;
  const total = documents?.total ?? 0;
  const byParty = documents?.counts.byParty ?? [];
  const byOrigin = documents?.counts.byOrigin ?? [];

  return (
    <div className={styles.scrim} onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Dokumente · ${contactName}`}
        tabIndex={-1}
        className={clsx(styles.panel, portal && styles.panelWide)}
      >
        <div className={styles.head}>
          <div className={styles.headText}>
            <Eyebrow size={11}>Dokumente · {contactName}</Eyebrow>
            <div className={styles.title}>
              {loading ? (
                <Skeleton width={220} height={16} />
              ) : portal ? (
                portal.documents.summary
              ) : (
                `${total} aus Kontakt, Deals und Portal`
              )}
            </div>
          </div>
          <span className={styles.headActions}>
            <Button variant="secondary" size="small" onClick={onUpload}>
              Hochladen
            </Button>
            <Button size="small" onClick={onRequest}>
              Anfordern
            </Button>
            <button type="button" className={styles.close} aria-label="Schließen" onClick={onClose}>
              ×
            </button>
          </span>
        </div>
        <div className={clsx(styles.body, portal && styles.bodyPortal)}>
          <nav className={styles.nav} aria-label="Dokumente filtern">
            <div className={styles.navLabel}>Nach Partei</div>
            <div className={styles.navGroup}>
              <button
                type="button"
                className={clsx(styles.partyChip, party === 'all' && styles.partyChipActive)}
                aria-pressed={party === 'all'}
                onClick={() => setParty('all')}
              >
                <span className={styles.chipLabel}>Alle</span>
                <span className={styles.chipCount}>{total}</span>
              </button>
              {byParty.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  className={clsx(styles.partyChip, party === entry.key && styles.partyChipActive)}
                  aria-pressed={party === entry.key}
                  onClick={() => setParty(party === entry.key ? 'all' : entry.key)}
                >
                  <span className={styles.chipLabel}>{entry.label}</span>
                  <span className={styles.chipCount}>{entry.count}</span>
                </button>
              ))}
            </div>
            {byOrigin.length > 0 ? (
              <>
                <div className={clsx(styles.navLabel, styles.navLabelSecond)}>Nach Objekt</div>
                <div className={styles.navGroup}>
                  {byOrigin.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      className={clsx(styles.originRow, origin === entry.key && styles.originRowActive)}
                      aria-pressed={origin === entry.key}
                      onClick={() => setOrigin(origin === entry.key ? 'all' : entry.key)}
                    >
                      <span className={styles.chipLabel}>{entry.label}</span>
                      <span className={styles.originCount}>{entry.count}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </nav>
          <div className={styles.table}>
            {portal ? (
              <div className={clsx(styles.tableHead, styles.tableHeadPortal)} role="row">
                <span>Dokument</span>
                <span>Quelle</span>
                <span>Status</span>
                <span>Im Portal sichtbar</span>
              </div>
            ) : (
              <div className={styles.tableHead} role="row">
                <span>Dokument</span>
                <span>Herkunft</span>
                <span>Stand</span>
                <span>Status</span>
              </div>
            )}
            {error ? (
              <div className={styles.tableNote}>
                <Banner tone="danger">{errorMessage(error)}</Banner>
              </div>
            ) : null}
            {loading ? (
              <div className={styles.tableNote}>
                <Skeleton width="60%" />
              </div>
            ) : null}
            {!loading && !error && rows.length === 0 ? (
              <div className={styles.tableNote}>Keine Dokumente.</div>
            ) : null}
            {portal
              ? rows.map((row) => (
                  <div key={row.id} className={clsx(styles.row, styles.rowPortal)}>
                    <button
                      type="button"
                      className={styles.rowOpen}
                      onClick={() => onOpenRow(row)}
                      aria-label={`${row.name} öffnen`}
                    >
                      <span className={styles.rowName}>{row.name}</span>
                      <span className={styles.rowMeta}>{row.meta}</span>
                    </button>
                    <span className={styles.rowSource}>{row.sourceLabel}</span>
                    <span className={styles.rowStatus}>
                      <Badge tone={row.tone}>{row.statusLabel}</Badge>
                    </span>
                    <VisibilityCell row={row} contactId={portal.contactId} />
                  </div>
                ))
              : rows.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    className={styles.row}
                    onClick={() => onOpenRow(row)}
                    aria-label={`${row.name} öffnen`}
                  >
                    <span className={styles.rowText}>
                      <span className={styles.rowName}>{row.name}</span>
                      <span className={styles.rowMeta}>{row.meta}</span>
                    </span>
                    <span
                      className={clsx(styles.rowOrigin, row.origin !== 'Kontakt' && styles.rowOriginAccent)}
                    >
                      {row.origin}
                    </span>
                    <span className={styles.rowDate}>{formatDate(row.uploadedAt)}</span>
                    <span>
                      <Badge tone={row.tone}>{row.statusLabel}</Badge>
                    </span>
                  </button>
                ))}
          </div>
          {portal ? (
            <PortalDocumentsSidebar documents={portal.documents} onRequest={onRequestPortal ?? onRequest} />
          ) : null}
        </div>
      </div>
    </div>
  );
}
